import type { MouseEvent } from 'react';
import { Link } from 'wouter';
import { Truck, Clock, MapPin, Flower2, Gift } from 'lucide-react';
import { collectionUrl } from '@/lib/collectionUrl';

/* ── Tokens ─────────────────────────────────────────────── */
const BG       = '#ffffff';
const WHITE    = '#ffffff';
const DARK     = '#2A1F14';
const GOLD     = '#e05590';
const ROSE     = '#9B2335';
const MUTED    = 'rgba(42,31,20,0.55)';
const ICON_BG  = '#FAE8E8';

/* ── Per-flower content ──────────────────────────────────── */
const FLOWER_CONTENT: Record<string, { title: string; desc: string }> = {
  roze: {
    title: 'ŚWIEŻE RÓŻE Z DOSTAWĄ W LUBLINIE',
    desc: 'Zamów bukiet róż online i wybierz dogodny termin dostawy. Każdy bukiet przygotowujemy ze świeżych kwiatów z dbałością o każdy detal. Dostarczamy w Lublinie i okolicach, aby Twój gest dotarł na czas - prosto z serca.',
  },
  lilie: {
    title: "LILIE DOPASOWANE DO MIEJSCA WRĘCZENIA",
    desc: "Przed wyborem lilii pomyśl o miejscu, w którym staną kwiaty, oraz o upodobaniach odbiorcy do zapachów. O konkretną odmianę i możliwość przygotowania kompozycji na wybrany dzień zapytaj kwiaciarnię.",
  },
  gerbery: {
    title: "GERBERY NA KOLOROWY PREZENT",
    desc: "Zacznij od ulubionego koloru obdarowanej osoby. Na karcie kompozycji sprawdź, czy gerbery są głównym elementem, czy dodatkiem do innych kwiatów; sama nazwa kategorii nie opisuje całego bukietu.",
  },
  gozdziki: {
    title: "GOŹDZIKI W KLASYCZNYM LUB MIESZANYM BUKIECIE",
    desc: "O charakterze prezentu decyduje całe zestawienie, także oprawa i dodatki. Jeśli wręczacie kwiaty od kilku osób, porównajcie dostępne warianty zamiast zakładać, że każdy bukiet z goździkami ma tę samą wielkość.",
  },
  sloneczniki: {
    title: "SŁONECZNIKI W BUKIECIE CZY W KOSZU?",
    desc: "Wazon i miejsce na stole pomogą rozstrzygnąć wybór formy. Bukiet do osobistego wręczenia i kompozycja w koszu mogą wyglądać podobnie kolorystycznie, ale warto porównać ich oprawę oraz wymiary podane w ofercie.",
  },
  alstroemerie: {
    title: "ROLA ALSTROEMERII W KOMPOZYCJI",
    desc: "Zdjęcie całego bukietu pozwala ocenić proporcje kwiatów lepiej niż nazwa jednego gatunku. Gdy zależy Ci szczególnie na alstroemeriach, sprawdź opis składu i przed zamówieniem wyjaśnij ewentualne wątpliwości.",
  },
  eustomy: {
    title: "EUSTOMY I STYL KWIATOWEGO PREZENTU",
    desc: "Nie musisz ograniczać wyboru do jednego gatunku. Zestawienie eustom z innymi kwiatami może lepiej nawiązać do ulubionych barw odbiorcy. Przy zamówieniu na odległość przygotuj także treść osobistego bileciku.",
  },
  gipsowka: {
    title: "JAK OCENIĆ ILOŚĆ GIPSÓWKI W BUKIECIE",
    desc: "Na fotografii zwróć uwagę na proporcje gipsówki do pozostałych kwiatów. Jeśli oczekujesz konkretnego efektu, opisz go przy zapytaniu do kwiaciarni; nie zakładaj, że każda propozycja w tej kategorii jest bukietem samej gipsówki.",
  },
  margaretki: {
    title: "MARGARETKI NA MNIEJSZY LUB WIĘKSZY GEST",
    desc: "Niewielki upominek i prezent od całej rodziny wymagają innej skali. Porównując kompozycje, uwzględnij opis wybranego wariantu oraz to, czy odbiorca będzie przenosić kwiaty po spotkaniu.",
  },
  tulipany: {
    title: 'ŚWIEŻE TULIPANY Z DOSTAWĄ W LUBLINIE',
    desc: 'Zamów bukiet tulipanów online i wybierz termin dostawy w Lublinie. Wiosenne i świeże tulipany przygotowujemy ze starannie dobranych kwiatów z dbałością o każdy detal. Dostarczamy w Lublinie i okolicach.',
  },
  piwonie: {
    title: "PIWONIE NA ZAPLANOWANY TERMIN",
    desc: "Jeżeli zależy Ci właśnie na piwoniach, zacznij od potwierdzenia dostępności. Przy ograniczonej ofercie możesz zapytać o kompozycję w podobnej kolorystyce, zamiast zamawiać inny gatunek na podstawie samego zdjęcia inspiracyjnego.",
  },
  hortensje: {
    title: "HORTENSJE A PROPORCJE BUKIETU",
    desc: "Przy wyborze zwróć uwagę na całą formę kompozycji, nie tylko jej dominujący kwiat. Jeśli bukiet ma stać na niewielkim stole, zapytaj o wymiary wybranego wariantu, gdy opis produktu ich nie podaje.",
  },
  lewkonie: {
    title: "LEWKONIE DLA OSOBY, KTÓRA LUBI PACHNĄCE KWIATY",
    desc: "Zapach jest osobistą preferencją, szczególnie w małym pokoju lub wspólnym biurze. Jeśli nie znasz upodobań odbiorcy, opowiedz o miejscu wręczenia i poproś o pomoc w doborze kompozycji.",
  },
  frezje: {
    title: "FREZJE W PREZENCIE Z OSOBISTĄ WIADOMOŚCIĄ",
    desc: "Ulubiony kwiat może nawiązać do wspólnego wspomnienia. Przy takim prezencie dodaj kilka słów od siebie, a przed zakupem sprawdź, czy frezje występują w wybranym wariancie kompozycji.",
  },
  jaskier: {
    title: "JASKRY W WYBRANEJ KOLORYSTYCE",
    desc: "Zdjęcie inspiracyjne pomaga pokazać styl, ale nie potwierdza dostępności konkretnego koloru. Jeżeli planujesz kwiaty do dekoracji lub stroju, podaj preferowaną paletę i termin przy zapytaniu.",
  },
  anturium: {
    title: "ANTURIUM JAKO AKCENT ARANŻACJI",
    desc: "Przy kwiatach do wnętrza warto pomyśleć o tle, na którym staną. Opis i zdjęcie produktu pomogą rozpoznać rodzaj oferowanej kompozycji; sama nazwa anturium nie oznacza, że kupujesz roślinę w doniczce.",
  },
  lwiepaszcze: {
    title: "LWIE PASZCZE A KSZTAŁT KOMPOZYCJI",
    desc: "Smukły lub rozłożysty bukiet inaczej prezentuje się we wnętrzu. Jeśli kompozycja ma pasować do konkretnego wazonu, przygotuj jego wymiary i zapytaj o dopasowanie kwiatów przed zamówieniem.",
  },
  dalie: {
    title: "DALIE W KOLORACH PASUJĄCYCH DO ODBIORCY",
    desc: "Nie każdy prezent musi nawiązywać do tradycyjnej symboliki kwiatów. Ulubione kolory i styl domu to konkretne wskazówki przy wyborze dalii oraz towarzyszących im gatunków.",
  },
  kalia: {
    title: "KALIE NA PREZENT LUB UROCZYSTOŚĆ",
    desc: "Przeznaczenie kwiatów pomaga dobrać oprawę. Bukiet do wręczenia i kompozycja na ceremonię wymagają innych ustaleń; przy zamówieniu opisz miejsce, charakter wydarzenia oraz oczekiwany termin.",
  },
};

/* ── Feature icons ──────────────────────────────────────── */
const FEATURES = [
  { Icon: Clock,   label: 'Termin dostawy\nsprawdź dostępność' },
  { Icon: MapPin,  label: 'Lublin\ni okolice' },
  { Icon: Flower2, label: 'Świeże kwiaty\nnajwyższej jakości' },
  { Icon: Gift,    label: 'Bilecik z życzeniami\nw cenie bukietu' },
] as const;

/* ── Pill links ─────────────────────────────────────────── */
const PILLS = [
  { label: 'Róże',            href: collectionUrl('roze') },
  { label: 'Tulipany',        href: collectionUrl('tulipany') },
  { label: 'Lilie',           href: collectionUrl('lilie') },
  { label: 'Hortensje',       href: collectionUrl('hortensje') },
  { label: 'Goździki',        href: collectionUrl('gozdziki') },
  { label: 'Eustomy',         href: collectionUrl('eustomy') },
  { label: 'Wszystkie kwiaty', href: collectionUrl('kwiaty') },
] as const;

/* ── Component ──────────────────────────────────────────── */
interface Props { handle: string }

export function FlowerDeliveryBanner({ handle }: Props) {
  const content = FLOWER_CONTENT[handle];
  if (!content) return null;

  return (
    <section aria-label="Dostawa kwiatów w Lublinie">

      {/* ── Delivery strip ──────────────────────────────── */}
      <div className="page-container" style={{
        background: WHITE,
        borderTop: '1px solid rgba(42,31,20,0.10)',
        paddingBlock: 'clamp(28px, 4vw, 44px)',
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(24px, 4vw, 56px)',
        flexWrap: 'wrap',
      }}>

        {/* Left: icon + title + description */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'clamp(14px, 2vw, 20px)', flex: '1 1 300px', minWidth: 0 }}>
          <div style={{
            flexShrink: 0,
            width: 'clamp(52px, 6vw, 68px)',
            height: 'clamp(52px, 6vw, 68px)',
            background: ICON_BG,
            border: `1px solid rgba(224,85,144,0.25)`,
            borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Truck size={28} color={GOLD} strokeWidth={1.5} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: '0 0 clamp(6px, 1vw, 10px)',
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 'clamp(10px, 1.1vw, 11.5px)',
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: DARK,
            }}>
              {content.title}
            </p>
            <p style={{
              margin: 0,
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 'clamp(12.5px, 1.3vw, 13.5px)',
              lineHeight: 1.75,
              color: MUTED,
            }}>
              {content.desc}
            </p>
          </div>
        </div>

        {/* Right: 4 feature boxes */}
        <div style={{
          display: 'flex',
          gap: 'clamp(16px, 2.5vw, 32px)',
          flexShrink: 0,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}>
          {FEATURES.map(({ Icon, label }) => (
            <div key={label} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              minWidth: 72,
              maxWidth: 100,
              textAlign: 'center',
            }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                border: `1px solid rgba(224,85,144,0.25)`,
                background: ICON_BG,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon size={18} color={ROSE} strokeWidth={1.5} />
              </div>
              <span style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 'clamp(10px, 1.05vw, 11px)',
                lineHeight: 1.45,
                color: MUTED,
                whiteSpace: 'pre-line',
              }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Pill bar ─────────────────────────────────────── */}
      <div className="page-container" style={{
        background: BG,
        borderTop: '1px solid rgba(210,60,120,0.18)',
        paddingBlock: 'clamp(20px, 3.5vw, 32px)',
      }}>
        <p style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 9,
          letterSpacing: '0.38em',
          textTransform: 'uppercase',
          color: GOLD,
          textAlign: 'center',
          margin: '0 0 clamp(12px, 2vw, 16px)',
        }}>
          Najczęściej wybierane kwiaty
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 'clamp(8px, 1.2vw, 10px)',
        }}>
          {PILLS.map(({ label, href }) => (
            <Link
              key={label}
              href={href}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '8px 16px',
                borderRadius: 999,
                border: '1px solid rgba(210,60,120,0.28)',
                background: '#ffffff',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 'clamp(11px, 1.2vw, 13px)',
                color: DARK,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(210,60,120,0.6)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 1px 6px rgba(42,31,20,0.08)';
              }}
              onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(210,60,120,0.28)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
              }}
            >
              <Flower2 size={13} color={ROSE} strokeWidth={1.8} />
              {label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Set of handles that use this banner ─────────────────── */
export const FLOWER_HANDLES = new Set(Object.keys(FLOWER_CONTENT));
